import { useEffect, useState } from "react";
import moment from "moment";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Content,
    ContentLoader,
    Icon,
    LoadingSpinner,
    Modal,
    ModalTitle,
    Row,
    SmallValueBox,
    Textarea,
} from "@webtypen/react-components";
import { ApplyForShiftForm, Calendar, Page } from "../components";
import { ApiHelper, ConfirmHelper } from "../helpers";

const ShiftElement = styled(Link)`
    padding: 10px 15px;
    border-bottom: 1px solid #eee;
    user-select: none;
    color: #676a6c;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;

    .shift-content {
        flex: 1;

        .date {
            font-weight: bold;
        }

        .infos {
            font-size: 12px;

            > span {
                font-weight: 500;
            }
        }
    }

    .shift-request {
        padding: 5px;
        border: 1px solid #eee;
        border-radius: 3px;

        .shift-request-label {
            font-size: 10px;
        }

        .shift-request-status {
            font-size: 12px;
            font-weight: 500;

            &.confirmed {
                color: ${(props) => props.theme.color.success};
            }

            &.declined {
                color: ${(props) => props.theme.color.danger};
            }
        }
    }

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: rgba(0, 0, 0, 0.03);
        text-decoration: none;
    }

    &:active {
        background: rgba(0, 0, 0, 0.075);
        text-decoration: none;
    }
`;

export const DashboardEmployee = () => {
    const auth = useSelector((state) => state.app.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [isApplying, setIsApplying] = useState(false);
    const [openShift, setOpenShift] = useState(null);
    const [data, setData] = useState(null);
    const [workingHoursStarting, setWorkingHoursStarting] = useState(false);

    const startWorkingHours = () => {
        setWorkingHoursStarting(true);

        ApiHelper.request("/api/working-hours/start")
            .then((response) => {
                if (response && response.status === "success" && response.data && response.data._id) {
                } else {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                }
                load();
                setWorkingHoursStarting(false);
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                load();
                setWorkingHoursStarting(false);
            });
    };

    const stopWorkingHours = () => {
        setWorkingHoursStarting(true);

        ApiHelper.request("/api/working-hours/stop")
            .then((response) => {
                if (response && response.status === "success" && response.data && response.data._id) {
                } else {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                }
                load();
                setWorkingHoursStarting(false);
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                load();
                setWorkingHoursStarting(false);
            });
    };

    const handleOpenShiftClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setOpenShift(e.currentTarget.getAttribute("data-id"));
    };

    const closeOpenShift = () => {
        setOpenShift(null);
    };

    const onApplySubmitEnd = (response) => {
        setIsApplying(false);

        if (response && response.status === "success") {
            setOpenShift(null);
            load();
        }
    };

    const load = () => {
        setIsLoading(true);

        ApiHelper.request("/api/dashboard/employee")
            .then((response) => {
                setData(response.status === "success" && response.data ? response.data : null);
                setIsLoading(false);
            })
            .catch((e) => {
                console.error(e);
                setData(null);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <Page title="Dashboard">
            {openShift ? (
                <Modal
                    open={true}
                    onCloseRequest={isApplying ? null : closeOpenShift}
                    contentAttributes={{
                        style: {
                            maxWidth: 550,
                            margin: "auto",
                            display: "flex",
                            maxHeight: "95vh",
                            flexDirection: "column",
                        },
                    }}
                >
                    <ModalTitle>
                        <Button
                            type="button"
                            className="float-right"
                            disabled={isApplying}
                            onClick={closeOpenShift}
                            light
                        >
                            <Icon icon={faTimes} />
                        </Button>
                        <h1>Für Job / Schicht eintragen</h1>
                    </ModalTitle>

                    <Content>
                        <ApplyForShiftForm
                            shiftId={openShift}
                            onSubmitStart={() => setIsApplying(true)}
                            onSubmitEnd={onApplySubmitEnd}
                        />
                    </Content>
                </Modal>
            ) : null}

            <Content>
                <Row>
                    {data && auth && auth.can_book_workingshours ? (
                        <Col width={4}>
                            <Card style={{ marginBottom: 15 }}>
                                {data.current_working_hours ? (
                                    <CardBody>
                                        <div style={{ paddingBottom: 15, textAlign: "center" }}>
                                            Gestartet:{" "}
                                            <b>
                                                {moment(data.current_working_hours.started_at).format(
                                                    "DD.MM.YYYY HH:mm"
                                                )}
                                            </b>{" "}
                                            Uhr
                                        </div>
                                        <Button
                                            color="warning"
                                            onClick={stopWorkingHours}
                                            disabled={workingHoursStarting}
                                            block
                                        >
                                            Arbeitszeit beenden
                                        </Button>
                                    </CardBody>
                                ) : (
                                    <CardBody>
                                        <div style={{ paddingBottom: 15, textAlign: "center" }}>
                                            Du hast gerade keine getsartete "Schicht".
                                        </div>
                                        <Button
                                            mode="primary"
                                            onClick={startWorkingHours}
                                            disabled={workingHoursStarting}
                                            block
                                        >
                                            Arbeitszeit starten
                                        </Button>
                                    </CardBody>
                                )}

                                {data.working_hours && data.working_hours.length > 0 ? (
                                    <div style={{}}>
                                        <div
                                            style={{
                                                fontWeight: "bold",
                                                paddingBottom: 5,
                                                paddingLeft: 10,
                                                paddingRight: 10,
                                            }}
                                        >
                                            Letzte Arbeitszeiten:
                                        </div>
                                        {data.working_hours.map((hour, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    padding: 10,
                                                    borderTop: "1px solid #eee",
                                                }}
                                            >
                                                <div style={{ fontWeight: "500" }}>
                                                    {hour.hours ? hour.hours : "0"} Stunden
                                                </div>
                                                {moment(hour.started_at).format("YYYY-MM-DD") ===
                                                moment(hour.ended_at).format("YYYY-MM-DD") ? (
                                                    <div style={{ fontSize: 12 }}>
                                                        {moment(hour.started_at).format("DD.MM.YYYY HH:mm")} Uhr
                                                        {" bis "}
                                                        {moment(hour.ended_at).format("HH:mm")} Uhr
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 12 }}>
                                                        {moment(hour.started_at).format("DD.MM.YYYY HH:mm")}
                                                        {" bis "}
                                                        {moment(hour.ended_at).format("DD.MM.YYYY HH:mm")} Uhr
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </Card>
                        </Col>
                    ) : null}

                    <Col width={12}>
                        <Row>
                            {/* <Col width={4} className="mb-3">
                                <SmallValueBox
                                    label="Deine Arbeitszeit diesen Monat"
                                    value={
                                        isLoading ? (
                                            <LoadingSpinner />
                                        ) : data && data.hours_this_month ? (
                                            NumericHelper.numberFormat(data.hours_this_month) + " h"
                                        ) : null
                                    }
                                />
                            </Col>
                            <Col width={4} className="mb-3">
                                <SmallValueBox
                                    label="Deine Arbeitszeit letzten Monat"
                                    value={
                                        isLoading ? (
                                            <LoadingSpinner />
                                        ) : data && data.hours_last_month ? (
                                            NumericHelper.numberFormat(data.hours_this_month) + " h"
                                        ) : null
                                    }
                                />
                            </Col> */}
                            <Col width={4} className="mb-3">
                                <Card>
                                    <CardBody>
                                        <SmallValueBox
                                            label="Verfügbare Urlaubstage"
                                            value={
                                                isLoading ? (
                                                    <LoadingSpinner />
                                                ) : auth && auth.vacation_days_total ? (
                                                    (auth.vacation_days_available
                                                        ? auth.vacation_days_available
                                                        : "0") +
                                                    " / " +
                                                    (auth.vacation_days_total ? auth.vacation_days_total : "0")
                                                ) : (
                                                    auth.vacation_days_available
                                                )
                                            }
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                    <Col width={4} className="mb-3">
                        <Card>
                            <CardHeader>Deine nächsten Jobs / Schichten</CardHeader>
                            {isLoading ? (
                                <CardBody>
                                    <ContentLoader />
                                </CardBody>
                            ) : data.next_shifts && data.next_shifts.length > 0 ? (
                                data.next_shifts.map((shift, i) => (
                                    <ShiftElement key={i} to={"/employees-shifts/" + shift._id + "/details"}>
                                        <div className="date">
                                            <span style={{ fontWeight: "500" }}>
                                                {moment(shift.date).format("DD.MM.YYYY")}
                                            </span>
                                            {": "}
                                            {shift.begin_planned ? shift.begin_planned : null}
                                            {shift.end_planned
                                                ? (shift.begin_planned ? " bis " : "") + shift.end_planned
                                                : null}
                                        </div>
                                        <div className="infos">
                                            <span className="ml-1">Kunde: </span>
                                            {shift.customer_object.name}

                                            {shift.type && shift.type_object && shift.type_object.name
                                                ? " (" + shift.type_object.name + ")"
                                                : null}
                                        </div>
                                    </ShiftElement>
                                ))
                            ) : (
                                <CardBody>Es sind keine vergangenen Jobs / Schichten verfügbar ...</CardBody>
                            )}
                        </Card>
                    </Col>
                    <Col width={4} className="mb-3">
                        <Card>
                            <CardHeader>Deine letzten Jobs / Schichten</CardHeader>
                            {isLoading ? (
                                <CardBody>
                                    <ContentLoader />
                                </CardBody>
                            ) : data.last_shifts && data.last_shifts.length > 0 ? (
                                data.last_shifts.map((shift, i) => (
                                    <ShiftElement key={i} to={"/employees-shifts/" + shift._id + "/details"}>
                                        <div className="date">
                                            <span style={{ fontWeight: "500" }}>
                                                {moment(shift.date).format("DD.MM.YYYY")}
                                            </span>
                                            {": "}
                                            {shift.begin_planned ? shift.begin_planned : null}
                                            {shift.end_planned
                                                ? (shift.begin_planned ? " bis " : "") + shift.end_planned
                                                : null}
                                        </div>
                                        <div className="infos">
                                            <span className="mr-1">Kunde: </span>
                                            {shift.customer_object.name}

                                            {shift.type && shift.type_object && shift.type_object.name
                                                ? " (" + shift.type_object.name + ")"
                                                : null}
                                        </div>
                                    </ShiftElement>
                                ))
                            ) : (
                                <CardBody>Es sind keine vergangenen Jobs / Schichten verfügbar ...</CardBody>
                            )}
                        </Card>
                    </Col>
                    <Col width={4} className="mb-3">
                        <Card>
                            <CardHeader>Offene Jobs / Schichten</CardHeader>

                            {isLoading ? (
                                <CardBody>
                                    <ContentLoader />
                                </CardBody>
                            ) : data.open_shifts && data.open_shifts.length > 0 ? (
                                data.open_shifts.map((shift, i) => (
                                    <ShiftElement
                                        key={i}
                                        to={"/dashboard"}
                                        data-id={shift._id}
                                        onClick={handleOpenShiftClick}
                                    >
                                        <div className="shift-content">
                                            <div className="date">
                                                <span style={{ fontWeight: "500" }}>
                                                    {moment(shift.date).format("DD.MM.YYYY")}
                                                </span>
                                                {": "}
                                                {shift.begin_planned ? shift.begin_planned : null}
                                                {shift.end_planned
                                                    ? (shift.begin_planned ? " bis " : "") + shift.end_planned
                                                    : null}
                                            </div>
                                            <div className="infos">
                                                <div>
                                                    <span className="mr-1">Kunde:</span>
                                                    {shift.customer_object.name}

                                                    {shift.type && shift.type_object && shift.type_object.name
                                                        ? " (" + shift.type_object.name + ")"
                                                        : null}
                                                </div>
                                                {shift.contract_object.address_city ? (
                                                    <div>
                                                        <span
                                                            style={{ display: "inline-block", width: 42 }}
                                                            className="mr-1"
                                                        >
                                                            Ort:
                                                        </span>
                                                        {shift.contract_object.address_city}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>

                                        {shift.shift_request && shift.shift_request._id ? (
                                            <div className="shift-request">
                                                <div className="shift-request-label">Bewerbung gesendet:</div>
                                                <div className={"shift-request-status " + shift.shift_request.status}>
                                                    {shift.shift_request.status === "pending"
                                                        ? "Offen"
                                                        : shift.shift_request.status === "declined"
                                                        ? "Abgelehnt"
                                                        : shift.shift_request.status}
                                                </div>
                                            </div>
                                        ) : null}
                                    </ShiftElement>
                                ))
                            ) : (
                                <CardBody>Es sind keine vergangenen Jobs / Schichten verfügbar ...</CardBody>
                            )}
                        </Card>
                    </Col>

                    <Col width={6}>
                        <Card>
                            <CardHeader>Kalender</CardHeader>
                            <Calendar />
                        </Card>
                    </Col>
                </Row>
            </Content>
        </Page>
    );
};
